import { useEffect, useState } from "react";
import { CalendarPlus, History } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FibiLoading } from "./FibiLoading";

const rp = (n) => `Rp${Number(n || 0).toLocaleString("id-ID")}`;
const PAY = [["tunai", "Tunai"], ["transfer", "Transfer"], ["saldo", "Saldo Agen"]];

export function PppRenewHistoryDialog({ complaint, mode = "history", onClose, onRenewed }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ months: 1, price: 0, payment_method: "tunai" });
  const [showRenew, setShowRenew] = useState(mode === "renew");
  const [busy, setBusy] = useState(false);
  const load = () => complaint && api.get(`/ppp/complaints/${complaint.id}/renew-history`).then((r) => { setData(r.data); setForm((f) => ({ ...f, price: r.data.price || 0 })); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setData(null); setShowRenew(mode === "renew"); load(); }, [complaint?.id, mode]); // eslint-disable-line
  if (!complaint) return null;
  const doRenew = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Perpanjang ${complaint.secret_name} ${form.months} bulan — ${rp(Number(form.price) * Number(form.months))}?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/routers/${complaint.router_id}/ppp/${encodeURIComponent(complaint.secret_name)}/perpanjang`, {
        months: Number(form.months), price: Number(form.price), payment_method: form.payment_method, customer: data?.customer || complaint.secret_name, sold_by: "", commissions: [],
      });
      toast.success(`Diperpanjang s/d ${fmtDate(r.expired_date)} — ${rp(r.sale?.total)}`);
      if (r.router_note) toast.info(`MikroTik: ${r.router_note}`, { duration: 8000 });
      setShowRenew(false); load(); onRenewed?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ppp-renew-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><History className="w-4 h-4 text-sky-400" /> Riwayat Perpanjangan</DialogTitle>
          <DialogDescription className="font-mono text-xs break-all">{complaint.secret_name} • {complaint.router_name}{data?.expired_date ? ` • exp ${fmtDate(data.expired_date)}` : ""}</DialogDescription>
        </DialogHeader>
        {!data ? <FibiLoading text="FIBI sedang memuat..." size={18} /> : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground" data-testid="ppp-renew-history-count">{data.history.length} transaksi tercatat</span>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => setShowRenew((v) => !v)} data-testid="ppp-renew-history-toggle-renew"><CalendarPlus className="w-3.5 h-3.5 mr-1" /> Perpanjang</Button>
            </div>
            {showRenew && (
              <form onSubmit={doRenew} className="grid grid-cols-3 gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3" data-testid="ppp-renew-form">
                <label className="text-xs space-y-1"><span className="text-muted-foreground">Bulan</span><Input type="number" min={1} max={24} value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} className="h-8" data-testid="ppp-renew-months" /></label>
                <label className="text-xs space-y-1"><span className="text-muted-foreground">Harga/bln</span><Input type="number" min={0} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-8" data-testid="ppp-renew-price" /></label>
                <label className="text-xs space-y-1"><span className="text-muted-foreground">Bayar</span>
                  <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="h-8 w-full rounded-md border bg-background px-2 text-xs" data-testid="ppp-renew-payment">{PAY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                </label>
                <div className="col-span-3 flex items-center justify-between text-xs">
                  <span>Total: <b className="font-mono" data-testid="ppp-renew-total">{rp(Number(form.price) * Number(form.months))}</b></span>
                  <Button type="submit" size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white" disabled={busy} data-testid="ppp-renew-submit">{busy ? "Memproses..." : "Perpanjang Sekarang"}</Button>
                </div>
              </form>
            )}
            {!data.history.length ? <p className="text-sm text-muted-foreground py-4 text-center" data-testid="ppp-renew-history-empty">Belum ada perpanjangan tercatat.</p> : (
              <ul className="divide-y text-xs" data-testid="ppp-renew-history-list">
                {data.history.map((h) => (
                  <li key={h.id} className="py-2 flex flex-wrap items-start justify-between gap-x-3 gap-y-0.5" data-testid={`ppp-renew-history-item-${h.id}`}>
                    <div>
                      <div className="font-mono">{fmtDateTime(h.at)}</div>
                      <div className="text-muted-foreground">{h.via}{h.by ? ` • ${h.by}` : ""}{h.payment_method ? ` • ${h.payment_method}` : ""}</div>
                      {h.expired_date && <div className="text-muted-foreground">{h.prev_expired_date ? `${fmtDate(h.prev_expired_date)} → ` : "exp "}{fmtDate(h.expired_date)}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-semibold">{rp(h.amount)}</div>
                      <div className={h.status === "lunas" ? "text-emerald-400" : h.status === "dibatalkan" ? "text-rose-400" : "text-amber-400"}>{h.status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
